// The chart styles a dashboard tile can be stored and rendered as.
//
// This list used to live in `apps/frontend/src/components/dashboards/
// composition/blocks.ts`, next to everything else about a block. It moved here
// on 2026-09-08 (plan 2026-09-08-one-drawing-layer-for-chat-dashboard-and-
// block-reports, A2) for one structural reason: `@verity/charts` keys its
// recipe map on `DashboardChartType` so that a new registry row is a COMPILE
// error rather than a chart that silently falls through to bars — and
// `blocks.ts` imports `@/api/composition`, the HTTP client. A package that may
// not import from `apps/` cannot key on a type that drags an API client behind
// it, so the literal moved to the boundary both sides may read.
//
// `packages/shared-types` already hosts this kind of cross-boundary literal
// (`composition-layout`); `blocks.ts` re-exports both names, so every existing
// app importer is unchanged.
//
// Mirrors `CHART_STYLES` in services/composition/blocks.py, which derives it
// from the registry; the backend test `test_composition_block_type_
// resolution.py::test_the_ts_mirror_of_chart_styles_is_equal` parses THIS file
// and fails when the two differ. The pre-registry hyphenated spellings
// (`horizontal-bar`, `stacked-bar`) stay out on purpose: a legacy tile keeps
// its legacy minimums (see `blockTypeOfSpec` in blocks.ts).

/** The chart styles a `chart` block renders as: the `dashboard_chart_type` of
 *  every chart-family visualization available on a dashboard.
 *
 *  REGISTRY ORDER, and the drift guard compares the two tuples positionally,
 *  so a new row goes where the registry puts it rather than at the end.
 *
 *  Keep prose OUT of the array body: that guard extracts every double-quoted
 *  token between the brackets, so a comment naming a style in quotes reads as
 *  an extra entry (measured while adding the funnel below, W4.3).
 *
 *  The funnel explorer joined in W4.3 (plan 2026-09-13-report-builder-parity-
 *  workstreams) after its browser gate. It is the first member of this list
 *  whose renderer is NOT the option builder: the route function answers with
 *  its own name and both surfaces early-return to their own component, which
 *  is why the builder's recipe map holds an entry for it that is built and
 *  discarded rather than one that draws. */
export const CHART_STYLES = [
  "line",
  "area",
  "line_smoothed",
  "area_stacked",
  "combo",
  "bar",
  "horizontal_bar",
  "stacked_bar",
  "bar_stacked_metrics",
  "bar_stacked_metrics_100",
  "grouped-bar",
  "bar_stacked_100",
  "waterfall",
  // Right after the waterfall that draws it, which is the registry order too.
  "bridge",
  // The step waterfall, drawn by the same renderer: registry order again
  // (plan 2026-10-16-a-waterfall-with-subtotals, A7).
  "metric_waterfall",
  // The receipt sits after the bridge for the same reason: registry order.
  "receipt",
  "pie",
  "donut",
  // The funnel explorer sits HERE, not at the end: see the note above.
  "funnel_explorer",
  "scatter",
  "bubble",
  "treemap",
  "sankey",
] as const;

/**
 * What a chart tile is stored and rendered as.
 *
 * DERIVED from `CHART_STYLES`, and that is the point. Until 2026-09-07 this
 * was a hand-written union in `chartSpecFromPreview.ts` that described itself
 * as "DOCUMENTATION", had no drift guard of its own, and whose only producer
 * cast into it (`configurableBlock.ts::dashboardChartTypeFor`). So a style
 * added to `CHART_STYLES` but not to the union compiled green, was cast in,
 * and any `Record<DashboardChartType, …>` keyed on it silently had no entry
 * for the new style — the same class of hole as a registry row without a
 * renderer branch, one level up. Deriving it makes the two impossible to
 * disagree.
 */
export type DashboardChartType = (typeof CHART_STYLES)[number];

/** The chart types a `kpi` block draws as a PICTURE (a bullet, a gauge)
 *  instead of the metric card: the `dashboard_chart_type` of every KPI row
 *  available on a dashboard, `metric_card` excluded (plan 2026-10-05 finance
 *  chart types, D1(a)).
 *
 *  Mirrors `KPI_CHART_TYPES` in services/composition/blocks.py, pinned equal
 *  by `test_composition_block_type_resolution.py`, which reads this literal
 *  the same way it reads `CHART_STYLES` above (keep prose out of the brackets).
 *
 *  Empty until the first KPI picture passes its browser gate and flips. A row
 *  still on `library` has its builder listed in `@verity/charts`'
 *  `PREPARED_LIBRARY_KPI_CHART_TYPES` instead, and leaves that list for this
 *  one at its flip. */
export const KPI_CHART_TYPES = ["bullet", "gauge"] as const;

/** The stored chart types whose query's SECOND dimension is part of the
 *  picture rather than a series split: a sankey's edges run from the first
 *  dimension's values to the second's (plan 2026-10-05 finance chart types,
 *  D5). A spec of one of these never carries `breakdown_key`.
 *
 *  Mirrors `structural_second_dimension_chart_types()` in
 *  api/constants/visualizations.py, pinned equal by
 *  `test_composition_block_type_resolution.py` (keep prose out of the
 *  brackets). By the row property, NOT by status: a library row is written
 *  the way it will be read after its flip. */
export const STRUCTURAL_SECOND_DIMENSION_CHART_TYPES = ["sankey"] as const;

/** The stored chart types whose breakdown splits the POINTS into series and
 *  keeps every metric (plan 2026-10-16-a-seasonal-heatmap-and-a-series-per-
 *  year, D3): a scatter's two axes and a bubble's size stay what they are,
 *  and the second dimension colours each point ("Colour by"). Every other
 *  breakdown row splits ONE metric; these are exempt from that rule.
 *
 *  Mirrors `point_split_breakdown_chart_types()` in
 *  api/constants/visualizations.py (the registry's `breakdown_splits_points`,
 *  which the served visualization list does not carry), pinned equal by
 *  `test_composition_block_type_resolution.py` (keep prose out of the
 *  brackets). */
export const POINT_SPLIT_BREAKDOWN_CHART_TYPES = ["scatter", "bubble"] as const;

/** The stored chart types whose metrics are the PARTS of one total, stacked
 *  per period (plan 2026-10-17 stacked metrics, D2/D3): every key is drawn,
 *  in the author's order, with no metric chips and no Best/Worst, and the
 *  write rule refuses two different known catalog formats among the parts.
 *
 *  Mirrors `parts_chart_types()` in api/constants/visualizations.py (the
 *  registry's `metrics_are_parts`), pinned equal by
 *  `test_composition_block_type_resolution.py`; the charts package's recipes
 *  (`metrics: "parts"`) are pinned equal to it by `metricChannels.test.ts`
 *  (keep prose out of the brackets). By the row property, NOT by status: a
 *  library row is written the way it will be read after its flip. */
export const PARTS_CHART_TYPES = ["bar_stacked_metrics", "bar_stacked_metrics_100"] as const;

/** What each stored chart type DRAWS that an editor can colour (plan
 *  2026-10-18 chart colours, D6): series kinds (`measure` is an `m:` ref,
 *  `dimension_value` a `d:` ref) and structural roles (`r:<role>`). A type
 *  absent from the map draws none (a table, a pivot, a receipt, a funnel
 *  explorer); the Format pane offers colours only for what is here. A metric
 *  card draws one measure, its sparkline (ADR-0476 §7, amended 2026-10-19).
 *
 *  Mirrors `colour_contract_for_chart_type()` in api/constants/visualizations.py
 *  (the registry's `colour_contract`), pinned equal by
 *  `test_composition_block_type_resolution.py`; the charts package pins it to
 *  what `seriesRefsOf` returns in `colourContract.test.ts` (keep prose out of
 *  the brackets). By the row property, NOT by status. Keys and arrays are
 *  sorted: the pin compares them as sets. */
export const CHART_COLOUR_CONTRACTS = {
  "area": { series: ["measure"], roles: [] },
  "area_stacked": { series: ["dimension_value", "measure"], roles: ["not_set", "other"] },
  "bar": { series: ["dimension_value", "measure"], roles: ["not_set", "other"] },
  "bar_stacked_100": { series: ["dimension_value", "measure"], roles: ["not_set", "other"] },
  "bar_stacked_metrics": { series: ["measure"], roles: [] },
  "bar_stacked_metrics_100": { series: ["measure"], roles: [] },
  "bridge": { series: [], roles: ["decrease", "increase", "total"] },
  "bubble": { series: ["dimension_value", "measure"], roles: ["not_set", "other"] },
  "bullet": { series: ["measure"], roles: ["target"] },
  "combo": { series: ["measure"], roles: [] },
  "donut": { series: ["dimension_value"], roles: ["not_set", "other"] },
  "gauge": { series: ["measure"], roles: ["target"] },
  "grouped-bar": { series: ["measure"], roles: [] },
  "horizontal_bar": { series: ["dimension_value", "measure"], roles: ["not_set", "other"] },
  "line": { series: ["dimension_value", "measure"], roles: ["not_set", "other"] },
  "line_smoothed": { series: ["dimension_value", "measure"], roles: ["not_set", "other"] },
  "metric_card": { series: ["measure"], roles: [] },
  "metric_waterfall": { series: [], roles: ["decrease", "increase", "total"] },
  "pie": { series: ["dimension_value"], roles: ["not_set", "other"] },
  "sankey": { series: ["dimension_value"], roles: ["not_set", "other"] },
  "scatter": { series: ["dimension_value", "measure"], roles: ["not_set", "other"] },
  "stacked_bar": { series: ["dimension_value", "measure"], roles: ["not_set", "other"] },
  "treemap": { series: ["dimension_value"], roles: ["not_set", "other"] },
  "waterfall": { series: [], roles: ["decrease", "increase", "total"] },
} as const;

/** What a KPI picture tile is stored as; derived for the same reason as
 *  `DashboardChartType`, so a builder map keyed on it cannot miss a row. */
export type KpiChartType = (typeof KPI_CHART_TYPES)[number];
